import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getTourDetail } from "../common/api";
import {
  clearPendingPayment,
  loadCheckoutDraft,
  loadPendingPayment,
  saveCheckoutDraft,
  savePendingPayment,
} from "../common/checkoutStorage";

const DEPOSIT_PER_ITEM = 20000;
const DEFAULT_MAX_CAPACITY = 45;

const PAYMENT_METHODS = [
  { id: "card", label: "Credit / Debit Card" },
  { id: "bank", label: "Bank Transfer" },
  { id: "vbank", label: "Virtual Account" },
];

function formatKRW(n) {
  return `₩${Number(n).toLocaleString("ko-KR")}`;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [state] = useState(() => {
    const incomingState = location.state ?? {};
    const hasIncomingOrder = incomingState.tourId
      || (Array.isArray(incomingState.cartItems) && incomingState.cartItems.length > 0);
    return hasIncomingOrder ? incomingState : (loadCheckoutDraft(user?.userId) ?? {});
  });

  // 단일 투어 or 장바구니 모드 구분
  const isCart = Array.isArray(state.cartItems) && state.cartItems.length > 0;
  const cartItems = state.cartItems ?? [];

  // 단일 투어 데이터
  const { tourId, title, date, persons, perPerson, total: singleTotal, optionValue } = state;

  // 결제 금액 계산
  const depositAmount = isCart ? cartItems.length * DEPOSIT_PER_ITEM : DEPOSIT_PER_ITEM;
  const tourTotal = isCart ? state.total : singleTotal;

  const [method, setMethod] = useState("card");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => (
    loadPendingPayment(user?.userId)
      ? "A previous payment was interrupted. Your order has been restored; please check it and try again."
      : null
  ));

  useEffect(() => {
    const hasOrder = isCart || Boolean(tourId);
    if (user && hasOrder) saveCheckoutDraft(state, user.userId);
  }, [isCart, state, tourId, user]);

  async function verifyLiveAvailability() {
    const requestedGroups = new Map();
    const requestedItems = isCart ? cartItems : [{ tourId, title, date, persons }];

    requestedItems.forEach((item) => {
      const itemTourId = item.tourId ?? item.tour_id;
      const itemDate = String(item.date ?? "").slice(0, 10);
      const itemPersons = Number(item.persons);
      if (!itemTourId || !itemDate || !Number.isFinite(itemPersons) || itemPersons < 1) {
        throw new Error("Invalid booking information. Please start over.");
      }

      const key = `${itemTourId}:${itemDate}`;
      const current = requestedGroups.get(key) ?? {
        tourId: itemTourId,
        date: itemDate,
        title: item.title ?? "this tour",
        persons: 0,
      };
      current.persons += itemPersons;
      requestedGroups.set(key, current);
    });

    await Promise.all(Array.from(requestedGroups.values()).map(async (request) => {
      const detail = await getTourDetail(request.tourId, request.date);
      const participantCount = Number(detail.participantCount ?? detail.participant_count ?? 0);
      const capacity = Number(detail.capacity ?? DEFAULT_MAX_CAPACITY);
      const remaining = Math.max(0, capacity - participantCount);

      if (request.persons > remaining) {
        throw new Error(
          remaining === 0
            ? `${request.title} is sold out for ${request.date}.`
            : `Only ${remaining} seat${remaining === 1 ? "" : "s"} left for ${request.title} on ${request.date}.`
        );
      }
    }));
  }

  useEffect(() => {
    if (!user) navigate("/");
    if (!isCart && !tourId) navigate("/");
  }, [user]);

  async function handlePay() {
    if (!agreed) return setError("Please agree to the terms and privacy policy");
    if (!depositAmount || depositAmount <= 0) return setError("Invalid order. Please start over.");
    setError(null);
    setLoading(true);

    try {
      await verifyLiveAvailability();

      // 예약 데이터 서버에 먼저 저장
      const sessionRes = await fetch(`${process.env.REACT_APP_API_URL}/payment/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(
          isCart
            ? { isCart: true, cartItems, userId: user.userId }
            : { tourId, date, persons, optionValue, total: singleTotal, userId: user.userId }
        ),
      });
      if (!sessionRes.ok) {
        throw new Error("Failed to save reservation");
      }
      const { sessionId } = await sessionRes.json();
      if (!sessionId) {
        throw new Error("Failed to save reservation");
      }

      const orderId = `ODK-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

      savePendingPayment({ sessionId, orderId, userId: user.userId });

      window.AUTHNICE.requestPay({
        clientId: process.env.REACT_APP_NICEPAY_CLIENT_ID,
        method,
        orderId,
        amount: depositAmount,
        goodsName: isCart ? `ODK Tour x${cartItems.length}` : title,
        returnUrl: `${process.env.REACT_APP_API_URL}/payment/confirm`,
        mallReserved: sessionId,  // sessionId만 전달 (짧음)
        buyerEmail: user.email,
        fnError: (result) => {
          clearPendingPayment();
          setLoading(false);
          setError(result.errorMsg || "Payment failed");
        },
      });
    } catch (err) {
      clearPendingPayment();
      setLoading(false);
      setError(err.message || "Failed to initialize payment. Please try again.");
    }
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-[900px] mx-auto">

        <button
          onClick={() => navigate(-1)}
          className="text-[13px] text-gray-400 hover:text-gray-600 mb-6 flex items-center gap-1"
        >
          ← Back
        </button>

        <h1 className="text-[24px] font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-[1fr_400px] gap-6 md:grid-cols-1">

          {/* 좌: 결제 수단 */}
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-[20px] border border-gray-100 p-6">
              <h2 className="text-[15px] font-bold text-gray-800 mb-4">Payment Method</h2>
              <div className="flex flex-col gap-3">
                {PAYMENT_METHODS.map((m) => (
                  <label
                    key={m.id}
                    className={`flex items-center gap-3 p-4 rounded-[12px] border-2 cursor-pointer transition-colors ${method === m.id ? "border-[#E8876A] bg-[#E8876A]/5" : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${method === m.id ? "border-[#E8876A]" : "border-gray-300"
                      }`}>
                      {method === m.id && <div className="w-2.5 h-2.5 rounded-full bg-[#E8876A]" />}
                    </div>
                    <input type="radio" className="hidden" value={m.id} checked={method === m.id} onChange={() => setMethod(m.id)} />
                    <span className="text-[14px] font-medium text-gray-700">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 동의 */}
            <div className="bg-white rounded-[20px] border border-gray-100 p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <div
                  onClick={() => setAgreed((v) => !v)}
                  className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${agreed ? "bg-[#E8876A] border-[#E8876A]" : "border-gray-300 bg-white"
                    }`}
                >
                  {agreed && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span className="text-[13px] text-gray-600 leading-relaxed">
                  I agree to the{" "}
                  <span className="text-[#E8876A] underline cursor-pointer" onClick={(e) => { e.stopPropagation(); window.open("/terms", "_blank"); }}>
                    Terms of Service
                  </span>{" "}and{" "}
                  <span className="text-[#E8876A] underline cursor-pointer" onClick={(e) => { e.stopPropagation(); window.open("/privacy", "_blank"); }}>
                    Privacy Policy
                  </span>
                </span>
              </label>
            </div>

            {error && <p className="text-[13px] text-red-400 px-1">{error}</p>}

            <button
              onClick={handlePay}
              disabled={loading}
              className="w-full py-4 bg-[#E8876A] text-white text-[15px] font-bold rounded-[14px] hover:bg-[#D96F52] transition-colors disabled:opacity-50 border-0"
            >
              {loading ? "Processing..." : `Pay Deposit ${formatKRW(depositAmount)}`}
            </button>
          </div>

          {/* 우: 주문 요약 */}
          <div className="flex flex-col gap-4">
            <div className="bg-white rounded-[20px] border border-gray-100 p-6">
              <h2 className="text-[15px] font-bold text-gray-800 mb-4">Order Summary</h2>

              {isCart ? (
                // 장바구니 모드
                <div className="flex flex-col gap-3">
                  {cartItems.map((item, i) => (
                    <div key={i} className="flex flex-col gap-1 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                      <div className="flex justify-between text-[13px]">
                        <span className="font-semibold text-gray-800">{item.title}</span>
                        <span className="font-medium text-gray-700">{formatKRW(item.total)}</span>
                      </div>
                      <div className="text-[11px] text-gray-400">
                        {String(item.date).slice(0, 10)} · {item.persons} person(s)
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-gray-100 pt-3 flex justify-between text-[13px]">
                    <span className="text-gray-500">Tour Total</span>
                    <span className="font-semibold text-gray-800">{formatKRW(tourTotal)}</span>
                  </div>
                </div>
              ) : (
                // 단일 투어 모드
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-gray-500">Tour</span>
                    <span className="font-semibold text-gray-800 text-right max-w-[180px]">{title}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium text-gray-700">{date}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-gray-500">Person(s)</span>
                    <span className="font-medium text-gray-700">{persons}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-gray-500">Price per person</span>
                    <span className="font-medium text-gray-700">{formatKRW(perPerson)}</span>
                  </div>
                  <div className="border-t border-gray-100 pt-3 flex justify-between text-[13px]">
                    <span className="text-gray-500">Tour Total</span>
                    <span className="font-semibold text-gray-800">{formatKRW(tourTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* 결제 금액 안내 */}
            <div className="bg-white rounded-[20px] border border-gray-100 p-6">
              <h2 className="text-[15px] font-bold text-gray-800 mb-4">Payment Breakdown</h2>
              <div className="flex flex-col gap-3">
                <div className="flex justify-between text-[13px]">
                  <span className="text-gray-500">
                    Deposit (due now){isCart ? ` × ${cartItems.length}` : ""}
                  </span>
                  <span className="font-bold text-[#E8876A]">{formatKRW(depositAmount)}</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-gray-500">Balance (due 5 days before tour)</span>
                  <span className="font-medium text-gray-700">{formatKRW(tourTotal - depositAmount)}</span>
                </div>
                <div className="border-t border-gray-100 pt-3 flex justify-between text-[14px]">
                  <span className="font-bold text-gray-800">Total</span>
                  <span className="font-bold text-gray-800">{formatKRW(tourTotal)}</span>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
                The balance will be automatically calculated based on the vehicle assigned 5 days before your tour date. You will receive an email with a payment link.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
